import { customerFacingEntityName } from './presentation';

export const genericVisualCategories = [
  'router',
  'router-core',
  'switch',
  'switch-access',
  'firewall',
  'wireless-ap',
  'wireless-controller',
  'server',
  'server-rack',
  'server-tower',
  'hypervisor',
  'virtual-machine',
  'container',
  'storage-array',
  'storage',
  'san',
  'nas',
  'database',
  'ups',
  'pdu',
  'ats',
  'generator',
  'power-meter',
  'crac',
  'crah',
  'ahu',
  'chiller',
  'pump',
  'fan',
  'environmental-sensor',
  'temperature-sensor',
  'humidity-sensor',
  'bacnet-controller',
  'modbus-device',
  'plc',
  'camera',
  'ip-phone',
  'printer',
  'workstation',
  'iot-device',
  'cloud',
  'internet',
  'site',
  'building',
  'floor',
  'room',
  'rack',
  'business-service',
  'application',
  'load-balancer',
  'unknown',
] as const;

export type AssetVisualClass = (typeof genericVisualCategories)[number];
export type VisualResolution =
  'EXACT_MODEL' | 'VENDOR_FAMILY' | 'VENDOR_TYPE' | 'GENERIC_TYPE' | 'UNKNOWN';
export type AssetVisualResolution = {
  visualId: string;
  visualClass: AssetVisualClass;
  pack: 'aot-core-generic';
  packVersion: '1.1.0';
  vendor: string;
  model: string;
  family: string;
  resolution: VisualResolution;
  artwork: 'AOT_GENERIC_SCHEMATIC';
  licenseStatus: 'AOT_OWNED';
  accessibleName: string;
};

export type VendorVisualRegistryEntry = {
  visualId: string;
  vendor?: string;
  modelMatch?: string;
  familyMatch?: string;
  category: AssetVisualClass;
  assetType?: string;
  iconAsset?: string;
  licenseStatus: string;
  approvedForProductUse: boolean;
  priority: number;
  version: number;
};

// Artwork approval is independent of identity classification. These entries intentionally fall back to AOT artwork.
export const vendorVisualRegistry: VendorVisualRegistryEntry[] = [
  {
    visualId: 'vendor-juniper-mx240',
    vendor: 'juniper',
    modelMatch: 'mx240',
    category: 'router-core',
    licenseStatus: 'PENDING_PERMISSION',
    approvedForProductUse: false,
    priority: 100,
    version: 1,
  },
  {
    visualId: 'vendor-eaton-93pm',
    vendor: 'eaton',
    modelMatch: '93pm',
    category: 'ups',
    licenseStatus: 'PENDING_PERMISSION',
    approvedForProductUse: false,
    priority: 100,
    version: 1,
  },
];

type ClassificationRule = {
  category: AssetVisualClass;
  vendors?: string[];
  modelTerms?: string[];
  familyTerms?: string[];
  assetTypes?: string[];
  sourceTerms?: string[];
  priority: number;
};
export const assetClassificationRegistry: ClassificationRule[] = [
  {
    category: 'router-core',
    vendors: ['juniper'],
    modelTerms: ['mx240', 'mx480', 'mx960'],
    priority: 200,
  },
  {
    category: 'switch',
    vendors: ['juniper'],
    familyTerms: ['ex'],
    modelTerms: ['ex2', 'ex3', 'ex4'],
    priority: 190,
  },
  { category: 'switch', vendors: ['cisco'], modelTerms: ['catalyst', 'nexus'], priority: 190 },
  { category: 'router', vendors: ['cisco'], modelTerms: ['isr', 'asr'], priority: 185 },
  {
    category: 'switch',
    vendors: ['aruba', 'hpe aruba'],
    modelTerms: ['cx ', 'procurve', 'aruba switch'],
    priority: 185,
  },
  {
    category: 'wireless-ap',
    vendors: ['aruba', 'ubiquiti'],
    modelTerms: ['access point', 'ap-', 'unifi ap'],
    priority: 185,
  },
  {
    category: 'wireless-controller',
    vendors: ['aruba', 'cisco', 'ubiquiti'],
    modelTerms: ['controller', 'mobility conductor', 'cloud key'],
    priority: 184,
  },
  {
    category: 'firewall',
    vendors: ['fortinet', 'palo alto networks', 'palo alto'],
    modelTerms: ['fortigate', 'pa-', 'firewall'],
    priority: 190,
  },
  {
    category: 'server-rack',
    vendors: ['hpe', 'hewlett packard enterprise'],
    modelTerms: ['proliant'],
    priority: 190,
  },
  {
    category: 'server-rack',
    vendors: ['dell', 'dell technologies'],
    modelTerms: ['poweredge'],
    priority: 190,
  },
  { category: 'storage-array', vendors: ['netapp'], priority: 175 },
  { category: 'nas', vendors: ['synology'], priority: 175 },
  {
    category: 'ups',
    vendors: ['eaton', 'apc', 'schneider electric', 'vertiv'],
    assetTypes: ['ups', 'facility-ups'],
    priority: 180,
  },
  {
    category: 'pdu',
    vendors: ['eaton', 'apc', 'schneider electric', 'vertiv'],
    assetTypes: ['pdu', 'facility-pdu'],
    priority: 180,
  },
  {
    category: 'switch',
    vendors: ['ruijie', 'reyee', 'mikrotik', 'ubiquiti'],
    assetTypes: ['switch', 'network-device'],
    priority: 170,
  },
  {
    category: 'router',
    vendors: ['ruijie', 'reyee', 'mikrotik', 'ubiquiti'],
    assetTypes: ['router'],
    priority: 170,
  },
  {
    category: 'hypervisor',
    vendors: ['proxmox', 'vmware', 'microsoft'],
    assetTypes: [
      'proxmox-node',
      'proxmox-cluster',
      'vmware-host',
      'vmware-vcenter',
      'hyperv-host',
      'hyperv-cluster',
    ],
    priority: 180,
  },
  { category: 'bacnet-controller', sourceTerms: ['bacnet'], priority: 165 },
  { category: 'modbus-device', sourceTerms: ['modbus'], priority: 165 },
];

const genericRules: Array<{ category: AssetVisualClass; terms: string[] }> = [
  { category: 'router-core', terms: ['core-router', 'core router'] },
  { category: 'router', terms: ['router'] },
  { category: 'switch-access', terms: ['access-switch', 'access switch'] },
  { category: 'switch', terms: ['switch'] },
  { category: 'firewall', terms: ['firewall', 'fortigate'] },
  { category: 'wireless-controller', terms: ['wireless-controller', 'wireless controller'] },
  { category: 'wireless-ap', terms: ['wireless-ap', 'access point'] },
  {
    category: 'hypervisor',
    terms: [
      'hypervisor',
      'proxmox-node',
      'proxmox-cluster',
      'vmware-host',
      'vmware-vcenter',
      'hyperv-host',
      'hyperv-cluster',
      'esxi',
    ],
  },
  {
    category: 'virtual-machine',
    terms: ['virtual-machine', 'proxmox-vm', 'vmware-vm', 'hyperv-vm'],
  },
  { category: 'container', terms: ['container', 'lxc'] },
  { category: 'server-rack', terms: ['rack-server', 'rack server', 'proliant', 'poweredge'] },
  { category: 'server', terms: ['server', 'host'] },
  { category: 'storage-array', terms: ['storage-array', 'storage array'] },
  { category: 'nas', terms: [' nas', 'nas-'] },
  { category: 'san', terms: [' san', 'san-'] },
  { category: 'storage', terms: ['storage', 'datastore', 'hyperv-csv'] },
  { category: 'ups', terms: ['facility-ups', ' ups', 'ups-'] },
  { category: 'pdu', terms: ['facility-pdu', ' pdu', 'pdu-'] },
  { category: 'ats', terms: [' ats', 'ats-'] },
  { category: 'generator', terms: ['generator'] },
  { category: 'power-meter', terms: ['power meter', 'power-meter'] },
  { category: 'crac', terms: ['crac'] },
  { category: 'crah', terms: ['crah'] },
  { category: 'ahu', terms: [' ahu', 'ahu-'] },
  { category: 'chiller', terms: ['chiller'] },
  { category: 'pump', terms: ['pump'] },
  { category: 'fan', terms: [' fan', 'fan-'] },
  { category: 'temperature-sensor', terms: ['temperature sensor', 'temperature-sensor'] },
  { category: 'humidity-sensor', terms: ['humidity sensor', 'humidity-sensor'] },
  {
    category: 'environmental-sensor',
    terms: ['facility-sensor', 'environmental sensor', 'ambient sensor'],
  },
  { category: 'bacnet-controller', terms: ['bacnet', 'building-controller'] },
  { category: 'modbus-device', terms: ['modbus'] },
  { category: 'plc', terms: [' plc', 'plc-'] },
  { category: 'camera', terms: ['camera'] },
  { category: 'ip-phone', terms: ['ip phone', 'phone'] },
  { category: 'printer', terms: ['printer'] },
  { category: 'workstation', terms: ['workstation', 'desktop'] },
  { category: 'iot-device', terms: ['iot'] },
  { category: 'cloud', terms: ['cloud'] },
  { category: 'internet', terms: ['internet', 'wan'] },
  { category: 'site', terms: ['site'] },
  { category: 'building', terms: ['building'] },
  { category: 'floor', terms: ['floor'] },
  { category: 'room', terms: ['room'] },
  { category: 'rack', terms: ['rack'] },
  { category: 'business-service', terms: ['business-service', 'business service'] },
  { category: 'application', terms: ['application', 'service'] },
  { category: 'load-balancer', terms: ['load balancer', 'load-balancer'] },
  { category: 'database', terms: ['database'] },
];

let runtimeRegistry: VendorVisualRegistryEntry[] = [];
export function setRuntimeAssetVisualRegistry(entries: VendorVisualRegistryEntry[]) {
  runtimeRegistry = entries
    .filter(
      (entry) => genericVisualCategories.includes(entry.category) && entry.approvedForProductUse,
    )
    .slice(0, 2_000);
}

const includesAny = (value: string, terms?: string[]) =>
  !terms?.length || terms.some((term) => value.includes(term));
function registryMatch(
  entry: VendorVisualRegistryEntry,
  vendor: string,
  model: string,
  family: string,
  type: string,
) {
  return (
    (!entry.vendor || vendor === entry.vendor.toLowerCase()) &&
    (!entry.modelMatch || model.includes(entry.modelMatch.toLowerCase())) &&
    (!entry.familyMatch || family.includes(entry.familyMatch.toLowerCase())) &&
    (!entry.assetType || type === entry.assetType.toLowerCase())
  );
}

export function resolveAssetVisual(input: any): AssetVisualResolution {
  const vendor = String(input?.vendor ?? input?.manufacturer ?? '').trim();
  const model = String(input?.model ?? input?.bacnetModel ?? '').trim();
  const family = String(input?.family ?? input?.productFamily ?? '').trim();
  const type = String(
    input?.visualClass ??
      input?.visual_class ??
      input?.assetType ??
      input?.asset_type ??
      input?.kind ??
      '',
  ).toLowerCase();
  const discovery = [
    input?.sysObjectID,
    input?.enterpriseOID,
    input?.sysDescr,
    input?.entityMib,
    input?.lldp,
    input?.bacnetVendorIdentifier,
    input?.bacnetModel,
    input?.modbusProfile,
    input?.source,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const vendorKey = vendor.toLowerCase();
  const modelKey = model.toLowerCase();
  const familyKey = family.toLowerCase();
  const configured = [...runtimeRegistry, ...vendorVisualRegistry]
    .sort((a, b) => b.priority - a.priority)
    .find((entry) => registryMatch(entry, vendorKey, modelKey, familyKey, type));
  const classified = assetClassificationRegistry
    .sort((a, b) => b.priority - a.priority)
    .find(
      (rule) =>
        (!rule.vendors?.length || rule.vendors.includes(vendorKey)) &&
        includesAny(modelKey, rule.modelTerms) &&
        includesAny(familyKey, rule.familyTerms) &&
        (!rule.assetTypes?.length || rule.assetTypes.includes(type)) &&
        includesAny(`${discovery} ${type}`, rule.sourceTerms),
    );
  const generic = genericRules.find((rule) =>
    rule.terms.some((term) => ` ${type} ${modelKey} ${familyKey} ${discovery} `.includes(term)),
  );
  const visualClass =
    configured?.category ?? classified?.category ?? generic?.category ?? 'unknown';
  const resolution: VisualResolution =
    configured?.modelMatch || (classified?.modelTerms?.length && modelKey)
      ? 'EXACT_MODEL'
      : configured?.familyMatch || (classified?.familyTerms?.length && familyKey)
        ? 'VENDOR_FAMILY'
        : vendor && (configured || classified)
          ? 'VENDOR_TYPE'
          : visualClass !== 'unknown'
            ? 'GENERIC_TYPE'
            : 'UNKNOWN';
  const approvedArtwork =
    configured?.approvedForProductUse && configured.iconAsset?.startsWith('aot-generic-');
  return {
    visualId: approvedArtwork ? configured.iconAsset! : `aot-generic-${visualClass}`,
    visualClass,
    pack: 'aot-core-generic',
    packVersion: '1.1.0',
    vendor,
    model,
    family,
    resolution,
    artwork: 'AOT_GENERIC_SCHEMATIC',
    licenseStatus: 'AOT_OWNED',
    accessibleName: [vendor, customerFacingEntityName(model || visualClass)].filter(Boolean).join(' '),
  };
}

// AOT Core Generic Pack: code-owned SVG geometry, no remote references or executable content.
export function AssetVisual({
  asset,
  size = 24,
  status,
  metric,
}: {
  asset: any;
  size?: number;
  status?: string;
  metric?: string;
}) {
  const visual = resolveAssetVisual(asset);
  const network = [
    'router',
    'router-core',
    'switch',
    'switch-access',
    'firewall',
    'wireless-ap',
    'wireless-controller',
    'load-balancer',
  ].includes(visual.visualClass);
  const facility = [
    'ups',
    'pdu',
    'ats',
    'generator',
    'power-meter',
    'crac',
    'crah',
    'ahu',
    'chiller',
    'pump',
    'fan',
    'bacnet-controller',
    'modbus-device',
    'plc',
  ].includes(visual.visualClass);
  const virtual = ['hypervisor', 'virtual-machine', 'container', 'cloud', 'internet'].includes(
    visual.visualClass,
  );
  const sensor = ['environmental-sensor', 'temperature-sensor', 'humidity-sensor'].includes(
    visual.visualClass,
  );
  return (
    <span
      className={`asset-visual asset-visual-${visual.visualClass} tone-${status ?? 'unknown'}`}
      style={{ width: size, height: size }}
      title={`${visual.accessibleName} · ${visual.artwork}`}
      role="img"
      aria-label={visual.accessibleName}
    >
      <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
        {network ? (
          <>
            <rect x="7" y="18" width="50" height="28" rx="5" />
            <path d="M15 27h34M15 36h8m5 0h8m5 0h8" />
            <circle cx="16" cy="41" r="2" />
          </>
        ) : facility ? (
          <>
            <rect x="14" y="7" width="36" height="50" rx="5" />
            <path d="M21 16h22M21 47h22" />
            <circle cx="32" cy="31" r="9" />
            <path d="M32 26v6l4 3" />
          </>
        ) : virtual ? (
          <>
            <rect x="9" y="12" width="38" height="34" rx="5" />
            <rect x="17" y="20" width="38" height="34" rx="5" />
            <path d="M27 31h18M27 39h12" />
          </>
        ) : visual.visualClass === 'rack' ? (
          <>
            <rect x="14" y="5" width="36" height="54" rx="3" />
            <path d="M20 14h24M20 23h24M20 32h24M20 41h24M20 50h24" />
          </>
        ) : sensor ? (
          <>
            <circle cx="32" cy="43" r="10" />
            <path d="M32 43V15M25 16a7 7 0 0 1 14 0v22" />
          </>
        ) : (
          <>
            <rect x="10" y="13" width="44" height="36" rx="5" />
            <path d="M18 23h28M18 32h28M18 41h18" />
            <circle cx="47" cy="41" r="2" />
          </>
        )}
      </svg>
      {metric && <small>{metric}</small>}
    </span>
  );
}
